import { useState, useEffect, MouseEvent, useCallback } from 'react'
import { Box, Flex, Container, Heading, Text, Link } from 'theme-ui'
import { RouteComponentProps, useLocation } from 'react-router-dom'
import { Nav, Paper, Loading, RouterLink } from 'components'
import {
  VerificationCodeForm,
  VerificationFormValues,
} from 'components/VerificationCodeForm'
import { useAuth } from 'providers/auth'
import getLocalized from 'i18n'
import { LocationState } from 'types'

const localized = getLocalized()

export function Verify({ match, history }: RouteComponentProps) {
  const location = useLocation<LocationState>()
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])
  // @ts-ignore
  const { code } = match.params
  const email = location?.state?.validEmail
  const enterCodeManually = code === undefined
  const { verify, loading, user, login } = useAuth()
  const [isCodeInvalid, setIsCodeInvalid] = useState(false)
  const [sendingCode, setSendingCode] = useState<boolean>(false)
  const [codeSent, setCodeSent] = useState<boolean>(false)
  const [verificationCode, setVerificationCode] = useState(code)
  async function bootstrap() {
    try {
      await verify(`${verificationCode}`)
    } catch (e) {
      setIsCodeInvalid(true)
    } finally {
      if (!user) {
        setIsCodeInvalid(true)
      }
    }
  }

  useEffect(() => {
    if (loading === false && user !== null) {
      const afterLoginPage = location.state?.from?.pathname
        ? location.state.from.pathname + location.state.from.search
        : '/'
      history.push(afterLoginPage)
    }
    // eslint-disable-next-line
  }, [user])

  useEffect(() => {
    if (verificationCode !== undefined) {
      let didCancel = false
      if (!didCancel) bootstrap()
      return () => {
        didCancel = true
      }
    }
    // eslint-disable-next-line
  }, [verificationCode])

  async function resendCode(e: MouseEvent<any>) {
    e.preventDefault()
    if (!email) {
      history.push({
        pathname: '/verify',
        state: { ...location?.state },
      })
      return
    }
    if (sendingCode) return
    setSendingCode(true)
    await login(email)
    setSendingCode(false)
    setCodeSent(true)
  }

  const handleVerificationSubmit = useCallback(
    (values: VerificationFormValues) => {
      setVerificationCode(values.code)
      setCodeSent(false)
    },
    [setVerificationCode]
  )

  const invalidCodeMessage = (
    <>
      <Text variant="text.warning">
        The verification code{' '}
        {verificationCode && <strong>{verificationCode} </strong>}has expired.
      </Text>
      <Flex
        sx={{
          mt: 4,
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        {!sendingCode && !codeSent && email && (
          <Link href="#" variant="links.router" onClick={resendCode}>
            <span>Send new verification code</span>
          </Link>
        )}
        {!sendingCode && !codeSent && !email && (
          <RouterLink to="/login" variant="links.router">
            <span>Send new verification code</span>
          </RouterLink>
        )}
        {sendingCode && !codeSent && (
          <>
            <Text sx={{ fontWeight: 'bold' }}>Sending the code</Text>
            <Box sx={{ pt: 1, ml: 3 }}>
              <Loading />
            </Box>
          </>
        )}
        {codeSent && (
          <Text sx={{ fontWeight: 'bold' }}>
            The code has been sent to {email}
          </Text>
        )}
      </Flex>
    </>
  )

  return (
    <>
      <Nav />
      <Box p={2} bg="primary" sx={{ height: '80vh' }}>
        <Container
          data-cy="verify-screen"
          sx={{ display: 'flex', flex: 1, height: '100%' }}>
          <Flex
            sx={{
              maxWidth: 780,
              mx: 'auto',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}>
            <Paper
              sx={{
                position: 'relative',
                flex: 1,
                textAlign: 'center',
                minHeight: 330,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}>
              {enterCodeManually ? (
                <>
                  <Heading variant="text.h1" as="h1" sx={{ mb: 3 }}>
                    Log in to the TM App Admin Dashboard
                  </Heading>
                  <Text
                    variant="text.body"
                    data-cy="enter-verification-code-text">
                    To log into your account, enter the verification code we
                    sent to the email <strong>{email}</strong>
                  </Text>
                  <VerificationCodeForm
                    data-cy="verification-code-form"
                    onSubmit={handleVerificationSubmit}
                    loading={loading}
                    isCodeInvalid={isCodeInvalid}
                    invalidCodeMessage={invalidCodeMessage}
                  />
                </>
              ) : (
                <>
                  <Heading variant="text.h1" as="h1" sx={{ mb: 3 }}>
                    Verify code
                  </Heading>
                  <Box data-cy="verification-results-box">
                    <Text variant="text.body">
                      We're verifying your details. It will only take a few
                      seconds.
                    </Text>
                    <Box
                      sx={{
                        pt: 3,
                        minHeight: 64,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}>
                      {!isCodeInvalid && <Loading />}
                      {isCodeInvalid ? invalidCodeMessage : null}
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          </Flex>
        </Container>
      </Box>
    </>
  )
}
